import { useCallback, useRef, useState } from "react";
import { ReportData } from "../../../features/reports/domain/models/report_data";
import { ReportMimeType } from "../../../common/utils/report_callback";
import { usePageLoader } from "../../../common/contexts/page_content_context";
import { processError } from "../../../common/utils/base_error";

/**
 * Custom types.
 */

export type ReportViewerCallback = (mimeType: ReportMimeType | null) => Promise<ReportData>;
export type ReportViewerConfig = {
  fetchCallback: ReportViewerCallback,
  closeReportModalCallback?: () => void,
};

/**
 * ReportViewerResponse
 */

export type ReportViewerResponse = {
  open: boolean;
  saving: boolean;
  reportData: ReportData | null;
  handleReportViewerSubmit: (mimeType: ReportMimeType) => void;
  handleReportViewerClose: () => void;
};

/**
 * useReportViewer
 * Retorno: [reportViewerState, startReportViewerProcess]
 */

export const useReportViewer = (): [ReportViewerResponse, (config: ReportViewerConfig) => void] => {
  const fetchCallbackRef = useRef<ReportViewerCallback | null>(null);
  const closeReportModalRef = useRef<(() => void) | null>(null);

  const {
    pushErrorNotification,
  } = usePageLoader();

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);

  const handleReportViewerClose = useCallback(() => {
    if (!saving) setOpen(false);
  }, [
    saving,
  ]);

  const handleReportViewerSubmit = useCallback(async (mimeType: ReportMimeType) => {
    if (!saving) {
      try {
        setSaving(true);
        await fetchCallbackRef.current?.(mimeType);
        fetchCallbackRef.current = null;
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatorio!'));
      } finally {
        closeReportModalRef.current?.();
        closeReportModalRef.current = null;

        setSaving(false);
        setOpen(false);
      }
    }
  }, [
    saving,
    pushErrorNotification,
  ]);

  const startReportViewerProcess = useCallback(async (config: ReportViewerConfig): Promise<void> => {
    if (!saving) {
      try {
        setReportData(await config.fetchCallback("text/html"));
        fetchCallbackRef.current = config.fetchCallback;
        closeReportModalRef.current = config.closeReportModalCallback ?? null;
        setOpen(true);
      } catch (e: unknown) {
        throw e;
      }
    }
  }, [
    saving,
  ]);

  return [
    {
      open,
      saving,
      reportData,
      handleReportViewerSubmit,
      handleReportViewerClose,
    },
    startReportViewerProcess,
  ];
}